import { getLocale, translate, translateRich, useLocale } from "../../localization";
import React, { useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppButton } from "../ui";
import "./SessionTimeline.scss";

/**
 * SessionTimeline
 * 
 * A Gantt-style visualization showing how students move through environments
 * during their sessions. Each row is a session, colored segments show
 * which environments were visited and when.
 * 
 * Decision it enables: "How do students spend their time across environments?"
 */
export default function SessionTimeline({ 
  detailedSessions = [], 
  sessions = [],
  environmentData = {},
  maxSessions = 30
}) {
  const locale = useLocale();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [sortBy, setSortBy] = useState("date"); // "date", "duration", "environments"
  const [selectedEnv, setSelectedEnv] = useState(null);

  // Generate consistent colors for environments
  const envColors = useMemo(() => {
    const colors = [
      "#8b5cf6", // purple
      "#3b82f6", // blue
      "#10b981", // green
      "#f59e0b", // amber
      "#ef4444", // red
      "#ec4899", // pink
      "#06b6d4", // cyan
      "#84cc16", // lime
      "#f97316", // orange
      "#6366f1", // indigo
      "#14b8a6", // teal
      "#a855f7", // violet
      "#eab308", // yellow
      "#22c55e", // emerald
      "#0ea5e9", // sky
    ];
    
    const allEnvIds = new Set();
    detailedSessions.forEach(session => {
      (session.events || []).forEach(event => {
        if (event.environmentId) {
          allEnvIds.add(event.environmentId);
        }
      });
    });
    
    const colorMap = {};
    Array.from(allEnvIds).forEach((envId, idx) => {
      colorMap[envId] = colors[idx % colors.length];
    });
    
    return colorMap;
  }, [detailedSessions, locale]);

  // Process sessions into timeline data
  const timelineData = useMemo(() => {
    if (!detailedSessions || detailedSessions.length === 0) {
      return { sessions: [], maxDuration: 0, environments: [] };
    }

    // Get session metadata from sessions array
    const sessionMeta = {};
    sessions.forEach(s => {
      const code = s.sessionCode || s.code;
      if (code) {
        sessionMeta[code.toLowerCase()] = {
          startTime: s.startTime || s.timestamp,
          totalDuration: s.secondsSpent || 0,
          meanScore: s.meanScore,
        };
      }
    });

    // Process each detailed session
    const processed = detailedSessions.map(session => {
      const sessionCode = session.sessionCode;
      const meta = sessionMeta[sessionCode?.toLowerCase()] || {};
      const events = session.events || [];
      
      // Sort events by timestamp
      const sortedEvents = [...events].sort((a, b) => {
        const timeA = new Date(a.sk || a.dateCreated || 0).getTime();
        const timeB = new Date(b.sk || b.dateCreated || 0).getTime();
        return timeA - timeB;
      });

      // Find session start time (earliest event)
      let sessionStart = null;
      sortedEvents.forEach(e => {
        const time = new Date(e.sk || e.dateCreated);
        if (!isNaN(time.getTime())) {
          if (!sessionStart || time < sessionStart) {
            sessionStart = time;
          }
        }
      });

      if (!sessionStart) {
        sessionStart = new Date(meta.startTime || Date.now());
      }

      // Build timeline segments
      const segments = [];
      let currentEnv = null;
      let segmentStart = null;
      
      sortedEvents.forEach((event, idx) => {
        const envId = event.environmentId;
        const eventTime = new Date(event.sk || event.dateCreated);
        
        if (isNaN(eventTime.getTime())) return;

        const secondsFromStart = Math.max(0, (eventTime - sessionStart) / 1000);

        // If environment changed or first event
        if (envId && envId !== currentEnv) {
          // Close previous segment
          if (currentEnv && segmentStart !== null) {
            segments.push({
              environmentId: currentEnv,
              environmentName: environmentData[currentEnv] || translate("Env {value0}", {
  value0: currentEnv.slice(0, 6)
}),
              startSeconds: segmentStart,
              endSeconds: secondsFromStart,
              color: envColors[currentEnv] || "#888",
            });
          }
          
          currentEnv = envId;
          segmentStart = secondsFromStart;
        }

        // For last event or assessment, mark the end
        if (idx === sortedEvents.length - 1 && currentEnv) {
          // Estimate end time (add 30 seconds or use total duration)
          const endTime = Math.max(
            secondsFromStart + 30,
            meta.totalDuration || secondsFromStart + 60
          );
          
          segments.push({
            environmentId: currentEnv,
            environmentName: environmentData[currentEnv] || translate("Env {value0}", {
  value0: currentEnv.slice(0, 6)
}),
            startSeconds: segmentStart,
            endSeconds: endTime,
            color: envColors[currentEnv] || "#888",
          });
        }
      });

      // Calculate total duration
      const totalDuration = segments.length > 0 
        ? Math.max(...segments.map(s => s.endSeconds))
        : meta.totalDuration || 0;

      // Get unique environments
      const uniqueEnvs = [...new Set(segments.map(s => s.environmentId))];

      return {
        sessionCode,
        startTime: sessionStart,
        date: sessionStart.toLocaleDateString(getLocale()),
        segments,
        totalDuration,
        environmentCount: uniqueEnvs.length,
        meanScore: meta.meanScore,
      };
    }).filter(s => s.segments.length > 0);

    // Sort sessions
    let sorted = [...processed];
    if (sortBy === "date") {
      sorted.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
    } else if (sortBy === "duration") {
      sorted.sort((a, b) => b.totalDuration - a.totalDuration);
    } else if (sortBy === "environments") {
      sorted.sort((a, b) => b.environmentCount - a.environmentCount);
    }

    // Limit sessions
    sorted = sorted.slice(0, maxSessions);

    // Find max duration for scaling
    const maxDuration = Math.max(...sorted.map(s => s.totalDuration), 60);

    // Get all unique environments for legend
    const allEnvs = {};
    sorted.forEach(s => {
      s.segments.forEach(seg => {
        if (!allEnvs[seg.environmentId]) {
          allEnvs[seg.environmentId] = {
            id: seg.environmentId,
            name: seg.environmentName,
            color: seg.color,
          };
        }
      });
    });

    return {
      sessions: sorted,
      maxDuration,
      environments: Object.values(allEnvs),
    };
  }, [detailedSessions, sessions, environmentData, envColors, sortBy, maxSessions, locale]);

  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMouseEnter = (event, segment, session, e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
    setHoveredEvent({ segment, session });
  };

  const handleMouseLeave = () => {
    setHoveredEvent(null);
  };

  const handleSessionClick = (sessionCode) => {
    navigate(`/sessions/${sessionCode}`);
  };

  const handleEnvFilter = (envId) => {
    setSelectedEnv(selectedEnv === envId ? null : envId);
  };

  // Filter sessions by selected environment
  const filteredSessions = useMemo(() => {
    if (!selectedEnv) return timelineData.sessions;
    return timelineData.sessions.filter(s => 
      s.segments.some(seg => seg.environmentId === selectedEnv)
    );
  }, [timelineData.sessions, selectedEnv, locale]);

  if (timelineData.sessions.length === 0) {
    return (
      <div className="sessionTimeline">
        <div className="stEmpty">
          <div className="stEmptyIcon">📊</div>
          <div className="stEmptyText">{translate("No session timeline data available")}</div>
          <div className="stEmptySub">{translate("Timeline will appear when sessions have environment visit data")}</div>
        </div>
      </div>
    );
  }

  // Time axis markers
  const timeMarkers = [];
  const markerInterval = timelineData.maxDuration > 600 ? 300 : 
                         timelineData.maxDuration > 300 ? 120 : 60;
  for (let t = 0; t <= timelineData.maxDuration; t += markerInterval) {
    timeMarkers.push(t);
  }

  return (
    <div className="sessionTimeline" ref={containerRef}>
      {/* Header */}
      <div className="stHeader">
        <div className="stSortControls">
          <span className="stSortLabel">{translate("Sort by:")}</span>
          <AppButton
            type="button"
            kind={sortBy === "date" ? "primary" : "secondary"}
            className={`stSortBtn ${sortBy === "date" ? "active" : ""}`}
            onClick={() => setSortBy("date")}
          >{translate("Date")}</AppButton>
          <AppButton
            type="button"
            kind={sortBy === "duration" ? "primary" : "secondary"}
            className={`stSortBtn ${sortBy === "duration" ? "active" : ""}`}
            onClick={() => setSortBy("duration")}
          >{translate("Duration")}</AppButton>
          <AppButton
            type="button"
            kind={sortBy === "environments" ? "primary" : "secondary"}
            className={`stSortBtn ${sortBy === "environments" ? "active" : ""}`}
            onClick={() => setSortBy("environments")}
          >{translate("Environments")}</AppButton>
        </div>
        <div className="stStats">
          <span className="stStatItem">{translateRich("{value0} sessions", {
  value0: <strong>{filteredSessions.length}</strong>
})}</span>
          <span className="stStatItem">{translateRich("{value0} environments", {
  value0: <strong>{timelineData.environments.length}</strong>
})}</span>
        </div>
      </div>

      {/* Legend */}
      <div className="stLegend">
        <span className="stLegendTitle">{translate("Environments:")}</span>
        <div className="stLegendItems">
          {timelineData.environments.map(env => (
            <AppButton
              key={env.id}
              type="button"
              kind="secondary"
              className={`stLegendItem ${selectedEnv === env.id ? "active" : ""} ${selectedEnv && selectedEnv !== env.id ? "dimmed" : ""}`}
              onClick={() => handleEnvFilter(env.id)}
              title={env.name}
            >
              <span 
                className="stLegendColor" 
                style={{ backgroundColor: env.color }}
              />
              <span className="stLegendName">
                {env.name.length > 15 ? env.name.slice(0, 13) + '...' : env.name}
              </span>
            </AppButton>
          ))}
        </div>
        {selectedEnv && (
          <AppButton
            className="stClearFilter"
            type="button"
            kind="danger"
            onClick={() => setSelectedEnv(null)}
          >{translate("Clear filter \xD7")}</AppButton>
        )}
      </div>

      {/* Timeline */}
      <div className="stTimelineContainer">
        {/* Time axis */}
        <div className="stTimeAxis">
          <div className="stTimeAxisLabel"></div>
          <div className="stTimeAxisTrack">
            {timeMarkers.map(t => (
              <div 
                key={t} 
                className="stTimeMarker"
                style={{ left: `${(t / timelineData.maxDuration) * 100}%` }}
              >
                <span className="stTimeMarkerLabel">{formatDuration(t)}</span>
              </div>
            ))}
          </div>
          <div className="stTimeAxisDuration">{translate("Duration")}</div>
        </div>

        {/* Session rows */}
        <div className="stRows">
          {filteredSessions.map((session) => (
            <div 
              key={session.sessionCode} 
              className="stRow"
              onClick={() => handleSessionClick(session.sessionCode)}
            >
              {/* Session label */}
              <div className="stRowLabel">
                <span className="stSessionCode">{session.sessionCode}</span>
                <span className="stSessionDate">{session.date}</span>
              </div>

              {/* Timeline bar */}
              <div className="stRowTrack">
                {/* Background grid lines */}
                {timeMarkers.map(t => (
                  <div 
                    key={t}
                    className="stGridLine"
                    style={{ left: `${(t / timelineData.maxDuration) * 100}%` }}
                  />
                ))}
                
                {/* Segments */}
                {session.segments.map((segment, idx) => {
                  const left = (segment.startSeconds / timelineData.maxDuration) * 100;
                  const width = ((segment.endSeconds - segment.startSeconds) / timelineData.maxDuration) * 100;
                  const isFiltered = selectedEnv && segment.environmentId !== selectedEnv;
                  
                  return (
                    <div
                      key={idx}
                      className={`stSegment ${isFiltered ? "dimmed" : ""}`}
                      style={{
                        left: `${left}%`,
                        width: `${Math.max(width, 0.5)}%`,
                        backgroundColor: segment.color,
                      }}
                      onMouseEnter={(e) => handleMouseEnter(e, segment, session, e)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </div>

              {/* Duration label */}
              <div className="stRowDuration">
                {formatDuration(session.totalDuration)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredEvent && (
        <div 
          className="stTooltip"
          style={{
            left: tooltipPos.x + 10,
            top: tooltipPos.y - 10,
          }}
        >
          <div className="stTooltipEnv">
            <span 
              className="stTooltipColor"
              style={{ backgroundColor: hoveredEvent.segment.color }}
            />
            {hoveredEvent.segment.environmentName}
          </div>
          <div className="stTooltipMeta">
            <span>{translateRich("Start: {value0}", {
  value0: formatDuration(hoveredEvent.segment.startSeconds)
})}</span>
            <span>{translateRich("End: {value0}", {
  value0: formatDuration(hoveredEvent.segment.endSeconds)
})}</span>
            <span>{translateRich("Duration: {value0}", {
  value0: formatDuration(hoveredEvent.segment.endSeconds - hoveredEvent.segment.startSeconds)
})}</span>
          </div>
          <div className="stTooltipSession">{translateRich("Session: {value0}", {
  value0: hoveredEvent.session.sessionCode
})}</div>
        </div>
      )}

      {/* Footer */}
      <div className="stFooter">
        <span>{translate("Click any session row to view details")}</span>
        <span>•</span>
        <span>{translate("Hover over segments for timing info")}</span>
      </div>
    </div>
  );
}
